import {useCallback, useState} from 'react';
import {Box, Stack, Typography} from '@mui/material';
import {useTranslation} from 'react-i18next';
import * as XLSX from 'xlsx';
import {wait} from '../../utils/functions/wait';
import {translations} from '../../i18n/translations';
import {useModalBtns} from './use-modal-btns';
import {Modal} from './modal';

type ModalXlsxProps = {
  open: boolean;
  onClose?: () => void;
  workBook?: XLSX.WorkBook;
  saveName?: string;
};

export function ModalXlsx({open, onClose, workBook, saveName}: ModalXlsxProps) {
  const {t} = useTranslation();
  const [loading, setLoading] = useState(false);
  const actionBtns = useModalBtns({
    left: {
      type: 'cancel',
      onClick: () => {
        onClose?.();
      },
    },
    right: {
      type: 'save',
      disabled: !workBook,
      onClick: async () => {
        try {
          if (!workBook) throw new Error('XLSX not available');
          setLoading(true);
          XLSX.writeFileXLSX(
            workBook,
            saveName ? `${saveName}.xlsx` : `${workBook.SheetNames[0]}.xlsx`
          );
          await wait(1000);
          onClose?.();
        } finally {
          setLoading(false);
        }
      },
      loading: loading,
    },
  });

  const refCallback = useCallback(
    (node: HTMLDivElement) => {
      if (node && workBook) {
        const sheet = workBook.Sheets[workBook.SheetNames[0]];
        const htmlStr = XLSX.utils.sheet_to_html(sheet);

        node.innerHTML = htmlStr;
      }
    },
    [workBook]
  );

  return (
    <Modal
      preventCloseClick
      fullWidth
      maxWidth="lg"
      open={open}
      onClose={onClose}
      actions={actionBtns}
    >
      <Stack
        sx={{
          overflow: 'auto',
          width: '100%',
          height: '80vh',
        }}
      >
        {workBook ? (
          <Box
            ref={refCallback}
            sx={{
              fontSize: '14.5px',
              '& table': {
                width: '100%',
                borderCollapse: 'collapse',
                color: 'gray.main',
              },
              '& tr td': {
                border: '0.5px solid #B6B6B6',
                padding: '4px 10px',
                whiteSpace: 'nowrap',
              },
              '& tr:first-child': {
                color: 'black',
                fontWeight: 'bold',
              },
            }}
          />
        ) : (
          <Stack height="100%" justifyContent="center" alignItems="center">
            <Typography variant="h6">{t(translations.loading_content.loading)}</Typography>
          </Stack>
        )}
      </Stack>
    </Modal>
  );
}
